import { useCallback } from "react"

import { useWebApp } from "@/features/webapp"

import { isPurchasableReport } from "../config/report-plans"
import { isReportPurchased, type ReportItem } from "../config/reports-data"
import { useUserReports } from "./use-user-reports"

export interface ReportRowState {
  /** Bought (any non-`purchasable` status) — shows "open" / counts as owned. */
  purchased: boolean
  /** Bought and still generating — the row shows the countdown card. */
  inProgress: boolean
  /** Display price — backend live price, else the catalog fallback. */
  price: number
}

/**
 * Per-report row state driven by the backend list (`GET /v1/reports/me`). For
 * purchasable reports (star/truths) status + price come from the server;
 * in-app reports (compatibility/palmistry) are always available. Single source
 * for every reports list (Today, My Plan, biorhythm, cross-sell).
 *
 * Until the backend entry is available (first load), a purchasable report falls
 * back to the subscription-entitlement flag so an owned report never briefly
 * shows "Get" (which a tap would turn into a second purchase).
 */
export function useReportStates(): (report: ReportItem) => ReportRowState {
  const { byType } = useUserReports()
  const {
    isStarReportPurchased,
    isTruthsReportPurchased,
    isSoulmateReportPurchased,
    isAstroMapReportPurchased,
    isAuraScanReportPurchased,
  } = useWebApp()

  return useCallback(
    (report: ReportItem): ReportRowState => {
      if (!isPurchasableReport(report.purchaseType)) {
        return { purchased: true, inProgress: false, price: report.price }
      }

      const api = byType(report.id)
      const entitled = isReportPurchased(report, {
        isStarReportPurchased,
        isTruthsReportPurchased,
        isSoulmateReportPurchased,
        isAstroMapReportPurchased,
        isAuraScanReportPurchased,
      })
      if (api) {
        return {
          // Entitlement covers main plans that include the report before/without
          // a matching row status (and OTP plans already in STAR_/…_PLAN_CODES).
          purchased: api.status !== "purchasable" || entitled,
          inProgress: api.status === "processing",
          price: api.price,
        }
      }

      // Backend list not loaded yet: keep the entitlement-based owned state.
      return {
        purchased: entitled,
        inProgress: false,
        price: report.price,
      }
    },
    [
      byType,
      isStarReportPurchased,
      isTruthsReportPurchased,
      isSoulmateReportPurchased,
      isAstroMapReportPurchased,
      isAuraScanReportPurchased,
    ],
  )
}
